import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hostingService, HostingPackage } from '../../services/hostingService';
import { useAuth } from '../../contexts/AuthContext';
import CheckoutSteps from '../../components/checkout/CheckoutSteps';
import DomainStep from '../../components/checkout/DomainStep';
import PlanStep from '../../components/checkout/PlanStep';
import PaymentStep from '../../components/checkout/PaymentStep';
import BillingAddressStep from '../../components/checkout/BillingAddressStep';
import ConfirmationStep from '../../components/checkout/ConfirmationStep';

export interface CheckoutData {
  packageId: string;
  packageName: string;
  domainName: string;
  domainAction: 'register' | 'transfer' | 'existing';
  billingCycle: 'monthly' | 'yearly';
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company?: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
  };
  pricing: {
    hostingPrice: number;
    domainPrice: number;
    discount: number;
    total: number;
  };
  customerType?: 'new' | 'existing' | 'unknown';
  isExistingCustomer?: boolean;
  isNewCustomerAccountCreated?: boolean;
  orderId?: string;
  orderNumber?: string;
  paymentIntentId?: string;
}

const HostingCheckout: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [hostingPackage, setHostingPackage] = useState<HostingPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  const [checkoutData, setCheckoutData] = useState<CheckoutData>({
    packageId: packageId || '',
    packageName: '',
    domainName: '',
    domainAction: 'register',
    billingCycle: 'monthly',
    contactInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US'
      }
    },
    pricing: {
      hostingPrice: 0,
      domainPrice: 12.99, // Standard domain registration price
      discount: 0,
      total: 0
    }
  });

  useEffect(() => {
    if (packageId) {
      fetchPackage();
    }
  }, [packageId]);

  // Check for existing login and populate user data
  useEffect(() => {
    const token = localStorage.getItem('token');
    const pendingCheckout = sessionStorage.getItem('pendingCheckout');
    
    if (token) {
      console.log('👤 User is signed in, loading profile and skipping email verification');
      
      // Load user profile for pre-population
      if (user) {
        setCheckoutData(prev => ({
          ...prev,
          contactInfo: {
            ...prev.contactInfo,
            firstName: user.firstName || '',
            lastName: user.lastName || '', 
            email: user.email || '',
            phone: user.phone || '',
            company: user.companyName || ''
          },
          customerType: 'existing',
          isExistingCustomer: true
        }));
      }
      
      // Restore pending checkout data if available
      if (pendingCheckout) {
        try {
          const pendingData = JSON.parse(pendingCheckout);
          
          if (pendingData.contactInfo) {
            setCheckoutData(prev => ({
              ...prev,
              contactInfo: pendingData.contactInfo,
              customerType: 'existing',
              isExistingCustomer: true
            }));
          }
          
          sessionStorage.removeItem('pendingCheckout');
        } catch (error) {
          console.error('Error restoring pending checkout:', error);
          sessionStorage.removeItem('pendingCheckout');
        }
      }
      
      // For signed-in users: Start at step 1 (which is now Plan step in their flow)
      setCurrentStep(1);
    }
  }, [user]);

  useEffect(() => {
    calculatePricing();
  }, [hostingPackage, checkoutData.billingCycle]);

  const fetchPackage = async () => {
    // Check if user is signed in - if not, show login prompt
    if (!user && !localStorage.getItem('token')) {
      setShowLoginPrompt(true);
      setLoading(false);
      // Still fetch package for UI display
      try {
        const result = await hostingService.getPackages();
        if (result.success) {
          const selectedPackage = result.data.find((pkg: HostingPackage) => pkg.id === packageId);
          if (selectedPackage) {
            setHostingPackage(selectedPackage);
            setCheckoutData(prev => ({
              ...prev,
              packageName: selectedPackage.name
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching package for login prompt:', error);
      }
      return;
    }

    try {
      const result = await hostingService.getPackages();
      if (result.success) {
        const selectedPackage = result.data.find((pkg: HostingPackage) => pkg.id === packageId);
        if (selectedPackage) {
          setHostingPackage(selectedPackage);
          setCheckoutData(prev => ({
            ...prev,
            packageName: selectedPackage.name
          }));
        } else {
          setError('Package not found');
        }
      } else {
        setError('Failed to fetch package details');
      }
    } catch (error) {
      setError('Failed to fetch package details');
    } finally {
      setLoading(false);
    }
  };

  const calculatePricing = () => {
    if (!hostingPackage) return;

    const basePrice = checkoutData.billingCycle === 'yearly' 
      ? parseFloat(String(hostingPackage.price_yearly || hostingPackage.price_monthly || '0')) 
      : parseFloat(String(hostingPackage.price_monthly || '0'));
    
    let discount = 0;
    if (checkoutData.billingCycle === 'yearly') {
      // 15% discount for yearly billing
      const monthlyTotal = parseFloat(String(hostingPackage.price_monthly || '0')) * 12;
      discount = monthlyTotal * 0.15;
    }

    // Calculate domain price based on action
    let domainPrice = 0;
    if (checkoutData.domainAction === 'register') {
      domainPrice = checkoutData.pricing?.domainPrice || 12.99; // Use actual price from domain check or default
    } else if (checkoutData.domainAction === 'transfer') {
      domainPrice = 12.99; // Transfer includes 1 year extension
    } else {
      domainPrice = 0; // Existing domain is free
    }
    
    const total = basePrice + domainPrice - discount;

    setCheckoutData(prev => ({
      ...prev,
      pricing: {
        hostingPrice: basePrice,
        domainPrice,
        discount,
        total
      }
    }));
  };

  const handleStepComplete = (stepData: any) => {
    setCheckoutData(prev => ({ ...prev, ...stepData }));
    setCurrentStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };

  const handleStepBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
    window.scrollTo(0, 0);
  };

  const handleLoginRedirect = () => {
    const currentUrl = window.location.pathname;
    const returnUrl = encodeURIComponent(currentUrl);
    navigate(`/login?return=${returnUrl}&message=Please sign in to purchase hosting`);
  };

  const LoginPrompt = () => (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      <div className="relative z-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-6 shadow-lg relative overflow-hidden mx-auto">
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
          <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Required</h2>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          To purchase hosting, you'll need a 12and22 account. This helps us manage your services and provide ongoing support.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleLoginRedirect}
            className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Sign In to Continue</span>
          </button>
          
          <button
            onClick={() => navigate('/register', { 
              state: { returnUrl: window.location.pathname }
            })}
            className="border-2 border-orange-400 text-orange-600 px-8 py-3 rounded-lg font-semibold text-lg hover:bg-gradient-to-br hover:from-orange-400 hover:via-yellow-500 hover:to-orange-600 hover:text-white hover:border-transparent transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:from-white/20 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Create Account</span>
          </button>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Why do I need an account?</h4>
          <ul className="text-sm text-blue-800 space-y-1 text-left">
            <li>• Manage your hosting account and settings</li>
            <li>• Access control panel and file manager</li>
            <li>• Receive important service notifications</li>
            <li>• Get priority customer support</li>
          </ul>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-4 shadow-lg relative overflow-hidden mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white relative z-10"></div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Loading Hosting Checkout</h3>
              <p className="text-gray-600">Preparing your hosting package...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !hostingPackage) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-2xl mb-4 shadow-lg relative overflow-hidden mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                <svg className="h-8 w-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Checkout Error</h3>
              <p className="text-gray-600 mb-6">{error || 'Package not found'}</p>
              <button 
                onClick={() => navigate('/hosting')}
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                <span className="relative z-10">Back to Hosting</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // F4 + F6: Single canonical step flow. Login is required to reach this
  // page (the LoginPrompt above intercepts non-signed-in users), so the
  // step labels match the actual rendered components 1:1.
  //
  // When the customer chose to REGISTER a new domain, we insert a Billing
  // Address step before Payment — AWS Route 53 Domains requires complete
  // WHOIS contact info or the registration is held in 'awaiting_admin'.
  // Transfers/existing-domain flows skip it.
  const needsBillingAddress = checkoutData.domainAction === 'register';
  const steps = needsBillingAddress
    ? [
        { number: 1, title: 'Domain', description: 'Choose domain option' },
        { number: 2, title: 'Plan', description: 'Billing preferences' },
        { number: 3, title: 'Billing', description: 'WHOIS contact info' },
        { number: 4, title: 'Payment', description: 'Secure payment' },
        { number: 5, title: 'Confirmation', description: 'Order complete' },
      ]
    : [
        { number: 1, title: 'Domain', description: 'Choose domain option' },
        { number: 2, title: 'Plan', description: 'Billing preferences' },
        { number: 3, title: 'Payment', description: 'Secure payment' },
        { number: 4, title: 'Confirmation', description: 'Order complete' },
      ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
            <span className="text-sm font-medium text-gray-700">Web Hosting</span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-4">
            Complete Your Order
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
              {hostingPackage.name}
            </span>
          </h1>
          <p className="text-xl text-gray-600">
            You're almost done! Let's get your website hosting set up
          </p>
        </div>

        {/* Progress Steps */}
        <CheckoutSteps steps={steps} currentStep={currentStep} />

        {/* Step Content */}
        <div className="mt-8">
          {showLoginPrompt ? (
            // F4: Non-signed-in users only see the login prompt. They cannot
            // advance into the wizard — login is enforced upstream so we never
            // try to auto-create accounts with random passwords.
            <LoginPrompt />
          ) : currentStep === 1 ? (
            <DomainStep
              data={checkoutData}
              onNext={handleStepComplete}
              onBack={() => navigate('/hosting')}
            />
          ) : currentStep === 2 ? (
            <PlanStep
              data={checkoutData}
              package={hostingPackage}
              onNext={handleStepComplete}
              onBack={handleStepBack}
            />
          ) : currentStep === 3 && needsBillingAddress ? (
            <BillingAddressStep
              data={checkoutData}
              onNext={handleStepComplete}
              onBack={handleStepBack}
            />
          ) : currentStep === 3 ? (
            <PaymentStep
              data={checkoutData}
              package={hostingPackage}
              onNext={handleStepComplete}
              onBack={handleStepBack}
            />
          ) : currentStep === 4 && needsBillingAddress ? (
            <PaymentStep
              data={checkoutData}
              package={hostingPackage}
              onNext={handleStepComplete}
              onBack={handleStepBack}
            />
          ) : currentStep === 4 ? (
            <ConfirmationStep
              data={checkoutData}
              package={hostingPackage}
            />
          ) : currentStep === 5 && needsBillingAddress ? (
            <ConfirmationStep
              data={checkoutData}
              package={hostingPackage}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default HostingCheckout;